import { useEffect } from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";

// Helper to detect if we're in local development environment
const isLocalDevelopment = () => {
  if (typeof globalThis.window === 'undefined') return false;

  const hostname = globalThis.window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isNamedDevelopmentHost = hostname === 'anuj.dev.sofmen.com';
  const isPrivateLanHost =
    /^192\.168\./.test(hostname) ||
    /^10\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);

  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';

  // In local development, skip redirects to allow testing without token
  return (isLocalhost || isNamedDevelopmentHost || isPrivateLanHost) && isDevelopment;
};

// Helper to treat refactor deployments like local development
const isRefactorEnvironment = () => process.env.REACT_APP_ENV === 'refactor';

/**
 * Root route guard that checks authentication before rendering any routes
 * This prevents the flash of public route content when navigating to /app
 * 
 * Logic:
 * - If no token: redirect to marketing URL immediately (no UI flash)
 * - If token exists and path is '/': redirect to '/stores' immediately (no landing page flash)
 * - If token exists and path is not '/': allow routes to render normally
 */
const RootRouteGuard = () => {
  const location = useLocation();
  
  // Synchronously check for token during render (before any child routes render)
  // This prevents the flash of public route content
  const token = typeof globalThis.window !== 'undefined' 
    ? localStorage.getItem("token") 
    : null;
  const isLocalDev = isLocalDevelopment() || isRefactorEnvironment();
  const isRootPath = location.pathname === '/';

  // Handle redirect in useEffect (can't redirect during render)
  useEffect(() => {
    if (isLocalDev) {
      return;
    }

    // If no token, redirect immediately before rendering anything
    // This prevents the flash of public route content
    if (!token) {
      const marketingUrl = process.env.REACT_APP_BASE_URL_MARKETING || '/';
      // Use replace to avoid adding to history and prevent back button issues
      globalThis.window.location.replace(marketingUrl);
      return;
    }

    // Note: Token + root path redirect is handled in render (below)
    // to prevent any flash of landing page UI
  }, [token, isLocalDev, location.pathname, isRootPath]);

  // Show nothing if no token (prevents flash of any UI)
  // This ensures no route content is rendered until auth check passes
  // Skip this check in local development to allow testing without token
  if (!isLocalDev && !token) {
    return null;
  }

  // If token exists and on root path, redirect to /stores immediately
  // This prevents landing page from rendering
  if (!isLocalDev && token && isRootPath) {
    return <Navigate to="/stores" replace />;
  }

  return <Outlet />;
};

export default RootRouteGuard;
